import React, { useState, useEffect, useReducer, useCallback } from "react";
import usePlanPermissions from "../../hooks/usePlanPermissions";
import ForbiddenPage from "../../components/ForbiddenPage";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Box,
  Chip,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  useMediaQuery,
  useTheme,
  TablePagination,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  CloudDownload as CloudDownloadIcon,
  Search as SearchIcon,
  Code as CodeIcon,
  Functions as FunctionsIcon,
  Extension as ExtensionIcon,
  Add as AddIcon,
  HelpOutline as HelpIcon,
  Android as SmartToyIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  VolumeUp as VolumeUpIcon,
  Mic as MicIcon,
  Settings as SettingsIcon,
  Memory as PsychologyIcon
} from "@mui/icons-material";

// Componentes
import useSettings from "../../hooks/useSettings";
import toastUtils from '../../utils/toastUtils';
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import AssistantModal from "./components/AssistantModal";
import ImportAssistantsModal from "./components/ImportAssistantsModal";
import AssistantsHelpModal from "./components/AssistantsHelpModal";
import TrainingModal from "./components/TrainingModal";

import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

// Reducer para gerenciar estado dos assistentes
const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_ASSISTANTS":
      return action.payload;
    case "UPDATE_ASSISTANT":
      const assistant = action.payload;
      const assistantIndex = state.findIndex((p) => p.id === assistant.id);
      if (assistantIndex !== -1) {
        state[assistantIndex] = assistant;
        return [...state];
      } else {
        return [assistant, ...state];
      }
    case "DELETE_ASSISTANT":
      const assistantId = action.payload;
      return state.filter((assistant) => assistant.id !== assistantId);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const Assistants = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const { getAll } = useSettings();
  const { permissions, loading: permissionsLoading } = usePlanPermissions();

  const [loading, setLoading] = useState(true);
  const [assistants, dispatch] = useReducer(reducer, []);
  const [assistantModalOpen, setAssistantModalOpen] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [filterModel, setFilterModel] = useState("");
  const [filterTool, setFilterTool] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [count, setCount] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [trainingModalOpen, setTrainingModalOpen] = useState(false);
  const [assistantForTraining, setAssistantForTraining] = useState(null);

  // Estados para verificar configurações globais
  const [hasGlobalConfig, setHasGlobalConfig] = useState(false);
  const [checkingGlobalConfig, setCheckingGlobalConfig] = useState(true);
  const [settings, setSettings] = useState({});

  // Estados para verificar integração Apollo
  const [apolloIntegrationEnabled, setApolloIntegrationEnabled] = useState(false);

  // Carregar configurações ao iniciar
  useEffect(() => {
    let isMounted = true;

    const loadSettings = async () => {
      try {
        const data = await getAll();
        if (!isMounted) return;

        const settingsObj = {};

        // Transformar array [{key, value}] em objeto {key: value}
        for (const { key, value } of data) {
          settingsObj[key] = value;
        }

        setSettings(settingsObj);
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
      }
    };

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  // Verificar configurações globais quando settings mudar
  useEffect(() => {
    if (Object.keys(settings).length > 0) {
      console.log("🔍 Settings carregadas:", {
        openAiKey: !!settings.openAiKey,
        apollioEnabled: settings.apollioEnabled,
        apollioApiKey: !!settings.apollioApiKey,
        calculoApollo: settings.apollioEnabled === "enabled" && !!settings.apollioApiKey
      });

      const hasConfig = !!settings.openAiKey;
      const apolloEnabled = settings.apollioEnabled === "enabled" && !!settings.apollioApiKey;

      // Batch state updates
      setHasGlobalConfig(hasConfig);
      setApolloIntegrationEnabled(apolloEnabled);
      setCheckingGlobalConfig(false);
    }
  }, [settings.openAiKey, settings.apollioEnabled, settings.apollioApiKey]);

  // Buscar assistentes com todos os filtros aplicados
  const fetchAssistants = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        searchParam,
        pageNumber: page + 1,
        pageSize: rowsPerPage,
      };

      if (filterModel) {
        params.model = filterModel;
      }

      if (filterTool) {
        params.toolType = filterTool;
      }

      const { data } = await api.get("/assistants", { params });
      dispatch({ type: "LOAD_ASSISTANTS", payload: data.assistants });
      setCount(data.count);
    } catch (err) {
      console.error(err);
      toastUtils.error("Erro nos Assistentes", i18n.t("assistants.toasts.loadError"));
    } finally {
      setLoading(false);
    }
  }, [searchParam, page, rowsPerPage, filterModel, filterTool]);

  // Carregar assistentes ao iniciar e quando mudar os filtros
  useEffect(() => {
    fetchAssistants();
  }, [fetchAssistants]);

  // Verificar permissão para usar Assistentes
  if (!permissionsLoading && !permissions.useAssistants) {
    return <ForbiddenPage />;
  }

  // Handlers para diversas ações
  const handleSearch = (event) => {
    setSearchParam(event.target.value);
    setPage(0);
  };

  const handleOpenAssistantModal = () => {
    setSelectedAssistant(null);
    setAssistantModalOpen(true);
  };

  const handleCloseAssistantModal = () => {
    setSelectedAssistant(null);
    setAssistantModalOpen(false);
  };

  const handleEditAssistant = (assistant) => {
    setSelectedAssistant(assistant);
    setAssistantModalOpen(true);
  };

  const handleConfirmDelete = (assistant) => {
    setSelectedAssistant(assistant);
    setConfirmModalOpen(true);
  };

  const handleDeleteAssistant = async (assistantId) => {
    try {
      await api.delete(`/assistants/${assistantId}`);
      toastUtils.success("Assistente Removido", i18n.t("assistants.toasts.deleted"));
      dispatch({ type: "DELETE_ASSISTANT", payload: assistantId });
      setCount((prevCount) => prevCount - 1);
    } catch (err) {
      toastUtils.error("Erro nos Assistentes", i18n.t("assistants.toasts.deleteError"));
    }
    setSelectedAssistant(null);
    setConfirmModalOpen(false);
  };

  const handleImportAssistants = () => {
    setImportModalOpen(true);
  };

  const handleImportComplete = () => {
    fetchAssistants();
  };

  const handleAssistantUpdated = () => {
    fetchAssistants();
  };

  const handleOpenTrainingModal = (assistant) => {
    setAssistantForTraining(assistant);
    setTrainingModalOpen(true);
  };

  const handleCloseTrainingModal = () => {
    setAssistantForTraining(null);
    setTrainingModalOpen(false);
  };

  const handleTrainingComplete = (trainingData) => {
    // Atualizar assistentes após treinamento
    fetchAssistants();
  };

  const handleCreateSDRAssistant = () => {
    // Criar um assistente com configurações pré-definidas para SDR
    const sdrAssistant = {
      name: "AI SDR Apollo",
      instructions: `Você é um assistente especializado em vendas e prospecção de leads (SDR - Sales Development Representative) com integração ao Apollo.io.

Suas principais responsabilidades incluem:

1. **Qualificação de Leads**: Analise informações de leads do Apollo.io e qualifique com base em critérios como:
   - Tamanho da empresa (número de funcionários)
   - Indústria e segmento de mercado
   - Localização geográfica
   - Tecnologias utilizadas
   - Orçamento estimado e autoridade de compra

2. **Pesquisa e Enriquecimento**: Utilize a integração com Apollo.io para:
   - Buscar informações detalhadas sobre empresas e contatos usando apollo_search_leads
   - Enriquecer perfis de leads com dados atualizados usando apollo_enrich_lead
   - Identificar tomadores de decisão nas organizações
   - Descobrir insights sobre a empresa (receita, crescimento, notícias)

3. **Personalização de Abordagem**: Crie mensagens personalizadas considerando:
   - O perfil específico do lead e sua empresa
   - Desafios comuns da indústria do lead
   - Como nosso produto/serviço pode resolver problemas específicos
   - Tom profissional mas acessível

4. **Gerenciamento de Campanhas**: Auxilie na gestão de campanhas SDR:
   - Liste campanhas existentes com apollo_list_campaigns
   - Crie novas campanhas com apollo_create_campaign
   - Monitore performance e resultados
   - Otimize estratégias baseadas em dados

5. **Monitoramento de Uso**: Acompanhe limites de uso do Apollo.io:
   - Verifique estatísticas de uso com apollo_get_usage_stats
   - Otimize buscas para não exceder limites
   - Alerte quando próximo dos limites mensais

6. **Relatórios e Insights**: Forneça análises sobre:
   - Performance de campanhas de prospecção
   - Taxas de resposta e conversão
   - Melhores horários e canais de contato
   - Sugestões de otimização baseadas em dados

**Funções Disponíveis:**
- apollo_search_leads: Busca prospects por cargo, localização, tamanho da empresa
- apollo_enrich_lead: Enriquece dados de um lead específico
- apollo_list_campaigns: Lista campanhas SDR existentes
- apollo_create_campaign: Cria nova campanha SDR
- apollo_get_usage_stats: Verifica uso atual do Apollo.io

Sempre seja objetivo, focado em resultados e orientado por dados. Utilize as informações do Apollo.io de forma estratégica para maximizar as chances de conversão.`,
      model: settings.openaiModel || "gpt-4o",
      active: true,
      voiceConfig: {
        enableVoiceResponses: false,
        enableVoiceTranscription: false,
        voiceId: 'nova',
        speed: 1.0,
        transcriptionModel: 'whisper-1',
        useStreaming: false
      }
    };

    // Pré-configurar as funções Apollo.io
    const apolloFunctions = [
      {
        name: "apollo_search_leads",
        description: "Busca leads/prospects usando Apollo.io com base em critérios específicos como cargo, localização e tamanho da empresa",
        parameters: {
          type: "object",
          properties: {
            job_titles: {
              type: "array",
              items: {
                type: "string"
              },
              description: "Lista de cargos/títulos para buscar (ex: ['CEO', 'CTO', 'Marketing Manager'])"
            },
            location: {
              type: "string",
              description: "Localização para busca (ex: 'Brazil', 'São Paulo', 'United States')"
            },
            company_size_ranges: {
              type: "array",
              items: {
                type: "string"
              },
              description: "Faixas de tamanho da empresa (ex: ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5001+'])"
            },
            keywords: {
              type: "string",
              description: "Palavras-chave para refinar a busca"
            },
            industries: {
              type: "array",
              items: {
                type: "string"
              },
              description: "Indústrias específicas para buscar"
            },
            max_results: {
              type: "integer",
              description: "Número máximo de resultados (limite: 50)",
              maximum: 50,
              default: 10
            }
          },
          required: ["job_titles"]
        }
      },
      {
        name: "apollo_enrich_lead",
        description: "Enriquece dados de um lead específico com informações adicionais do Apollo.io (email, telefone, etc.)",
        parameters: {
          type: "object",
          properties: {
            lead_id: {
              type: "string",
              description: "ID do lead no sistema interno"
            },
            apollo_id: {
              type: "string",
              description: "ID do lead no Apollo.io"
            }
          },
          anyOf: [
            { required: ["lead_id"] },
            { required: ["apollo_id"] }
          ]
        }
      },
      {
        name: "apollo_list_campaigns",
        description: "Lista as campanhas SDR existentes da empresa",
        parameters: {
          type: "object",
          properties: {
            limit: {
              type: "integer",
              description: "Número máximo de campanhas para retornar",
              default: 10,
              maximum: 50
            }
          }
        }
      },
      {
        name: "apollo_create_campaign",
        description: "Cria uma nova campanha SDR",
        parameters: {
          type: "object",
          properties: {
            name: {
              type: "string",
              description: "Nome da campanha"
            },
            description: {
              type: "string",
              description: "Descrição da campanha"
            },
            target_criteria: {
              type: "object",
              description: "Critérios de ICP (Ideal Customer Profile) para a campanha",
              properties: {
                jobTitles: {
                  type: "array",
                  items: { type: "string" }
                },
                location: { type: "string" },
                companySizeRanges: {
                  type: "array",
                  items: { type: "string" }
                },
                industries: {
                  type: "array",
                  items: { type: "string" }
                }
              }
            },
            qualification_rules: {
              type: "object",
              description: "Regras de qualificação de leads"
            },
            scheduling_config: {
              type: "object",
              description: "Configurações de agendamento de reuniões"
            }
          },
          required: ["name"]
        }
      },
      {
        name: "apollo_get_usage_stats",
        description: "Obtém estatísticas de uso atual do Apollo.io (buscas e enriquecimentos utilizados vs limites)",
        parameters: {
          type: "object",
          properties: {}
        }
      }
    ];

    // Incluir as funções Apollo.io e tools pré-configurados no assistant
    sdrAssistant.tools = [{ type: "function" }];
    sdrAssistant.functions = apolloFunctions;

    // Log para debug - remover em produção
    console.log("Dados do assistente SDR a ser criado:", sdrAssistant);

    setSelectedAssistant(sdrAssistant);
    setAssistantModalOpen(true);
  };


  // Configuração das ações do cabeçalho
  const pageActions = [
    {
      label: i18n.t("assistants.buttons.help"),
      icon: <HelpIcon />,
      onClick: () => setHelpModalOpen(true),
      variant: "outlined",
      color: "primary",
      tooltip: i18n.t("assistants.buttons.help")
    },
    {
      label: i18n.t("assistants.buttons.new"),
      icon: <AddIcon />,
      onClick: handleOpenAssistantModal,
      variant: "contained",
      color: "primary"
    },
    // Botão para criar assistente SDR pré-configurado
    // O agente SDR funciona SEM Apollo (modo importação manual) quando só OpenAI está configurada
    // Com Apollo habilitado, funciona também com discovery automática
    ...(hasGlobalConfig ? [{
      label: i18n.t("assistants.buttons.createSDR"),
      icon: <PsychologyIcon />,
      onClick: handleCreateSDRAssistant,
      variant: "contained",
      color: "secondary",
      tooltip: apolloIntegrationEnabled
        ? i18n.t("assistants.buttons.createSDRTooltip")
        : i18n.t("assistants.buttons.createSDRTooltipManual")
    }] : []),
    {
      label: i18n.t("assistants.buttons.import"),
      icon: <CloudDownloadIcon />,
      onClick: () => setImportModalOpen(true),
      variant: "outlined",
      color: "primary"
    }
  ];

  // Renderizar ícone da ferramenta
  const renderToolIcon = (toolType) => {
    switch (toolType) {
      case "file_search":
        return <SearchIcon fontSize="small" />;
      case "code_interpreter":
        return <CodeIcon fontSize="small" />;
      case "function":
        return <FunctionsIcon fontSize="small" />;
      default:
        return <ExtensionIcon fontSize="small" />;
    }
  };

  // Renderizar label da ferramenta
  const renderToolLabel = (toolType) => {
    switch (toolType) {
      case "file_search":
        return i18n.t("assistants.tools.fileSearch");
      case "code_interpreter":
        return i18n.t("assistants.tools.codeInterpreter");
      case "function":
        return i18n.t("assistants.tools.function");
      default:
        return toolType;
    }
  };

  // Renderizar indicador de configurações de voz
  const renderVoiceIndicator = (assistant) => {
    const hasVoiceConfig = assistant.voiceConfig && 
      (assistant.voiceConfig.enableVoiceResponses || assistant.voiceConfig.enableVoiceTranscription);
    
    if (!hasVoiceConfig) return null;
    
    return (
      <Tooltip title={i18n.t("assistants.tooltips.voiceEnabled")}>
        <Chip
          icon={assistant.voiceConfig.enableVoiceResponses ? <VolumeUpIcon fontSize="small" /> : <MicIcon fontSize="small" />}
          label={assistant.voiceConfig.enableVoiceResponses ? i18n.t("assistants.labels.voice") : i18n.t("assistants.labels.transcription")}
          size="small"
          variant="outlined"
          color="secondary"
          sx={{ margin: 0.25 }}
        />
      </Tooltip>
    );
  };

  // Renderizar indicador de configuração global
  const renderGlobalConfigIndicator = (settings) => {
    // Se não tem chave própria e existe configuração global, mostrar indicador
    if (settings && settings.openAiKey) {
      return (
        <Tooltip title={i18n.t("assistants.tooltips.globalConfig")}>
          <Chip
            icon={<SettingsIcon fontSize="small" />}
            label={i18n.t("assistants.labels.global")}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ margin: 0.25 }}
          />
        </Tooltip>
      );
    }
    return null;
  };

  // Renderizar conteúdo da página
  const renderContent = () => {
    if (assistants.length === 0 && !loading) {
      return (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          sx={{ height: '100%', p: 4 }}
        >
          <SmartToyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {activeTab === 0 
              ? i18n.t("assistants.emptyState.title")
              : activeTab === 1 
                ? i18n.t("assistants.emptyState.noActive")
                : i18n.t("assistants.emptyState.noInactive")
            }
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            {searchParam 
              ? i18n.t("assistants.emptyState.tryOtherTerms")
              : activeTab === 0
                ? i18n.t("assistants.emptyState.description")
                : i18n.t("assistants.emptyState.noneInCategory")
            }
          </Typography>
          {activeTab === 0 && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenAssistantModal}
                sx={{ borderRadius: '5px', px: 3 }}
              >
                {i18n.t("assistants.buttons.addEmpty")}
              </Button>
            </Box>
          )}
        </Box>
      );
    }

    // Tabela de assistentes
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="medium" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("assistants.table.name")}</TableCell>
                  <TableCell align="center">{i18n.t("assistants.table.model")}</TableCell>
                  <TableCell align="center">{i18n.t("assistants.table.tools")}</TableCell>
                  <TableCell align="center">{i18n.t("assistants.table.status")}</TableCell>
                  <TableCell align="center">{i18n.t("assistants.table.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assistants.map((assistant) => (
                  <TableRow key={assistant.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight={500}>
                        {assistant.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={assistant.model} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" flexWrap="wrap" gap={0.5}>
                        {assistant.tools && assistant.tools.map((tool) => (
                          <Tooltip
                            key={tool.type}
                            title={
                              tool.type === "file_search"
                                ? i18n.t("assistants.tools.fileSearchFull")
                                : tool.type === "code_interpreter"
                                ? i18n.t("assistants.tools.codeInterpreterFull")
                                : tool.type === "function"
                                ? i18n.t("assistants.tools.functionFull")
                                : tool.type
                            }
                          >
                            <Chip
                              icon={renderToolIcon(tool.type)}
                              label={renderToolLabel(tool.type)}
                              size="small"
                              variant="outlined"
                              sx={{ margin: 0.25 }}
                            />
                          </Tooltip>
                        ))}
                        {renderVoiceIndicator(assistant)}
                        {renderGlobalConfigIndicator(assistant)}
                        {(!assistant.tools || assistant.tools.length === 0) && 
                         !renderVoiceIndicator(assistant) && 
                         !renderGlobalConfigIndicator(assistant) && (
                          <Typography variant="caption" color="textSecondary">
                            {i18n.t("assistants.labels.none")}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={assistant.active ? i18n.t("assistants.status.active") : i18n.t("assistants.status.inactive")}
                        color={assistant.active ? "success" : "default"}
                        size="small"
                        sx={{ borderRadius: 12, fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        <Tooltip title={i18n.t("assistants.buttons.train")}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenTrainingModal(assistant)}
                            color="secondary"
                          >
                            <PsychologyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={i18n.t("assistants.buttons.edit")}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditAssistant(assistant)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={i18n.t("assistants.buttons.delete")}>
                          <IconButton
                            size="small"
                            onClick={() => handleConfirmDelete(assistant)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Box>
    );
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("assistants.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            size={isXs ? "medium" : "small"}
            placeholder={i18n.t("assistants.searchPlaceholder")}
            variant="outlined"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              margin: 0.5,
              backgroundColor: theme.palette.background.paper,
              borderRadius: isXs ? 8 : theme.shape.borderRadius,
              '& .MuiOutlinedInput-root': {
                borderRadius: isXs ? 12 : theme.shape.borderRadius,
                minHeight: isXs ? 48 : 40,
              },
              '& .MuiInputBase-input': {
                fontSize: isXs ? '1rem' : '0.875rem',
              }
            }}
          />
          {pageActions.map((action, index) => (
            <Button
              key={index}
              variant={action.variant || "contained"}
              color={action.color || "primary"}
              startIcon={action.icon}
              onClick={action.onClick}
              disabled={action.disabled || loading}
              fullWidth={isXs && action.primary}
              sx={{ margin: 0.5,}}
            >
              {action.label}
            </Button>
          ))}
        </MainHeaderButtonsWrapper>
      </MainHeader>
      {renderContent()}
      {/* Modais */}
      <ConfirmationModal
        title={selectedAssistant && `${i18n.t("assistants.confirmationModal.deleteTitle")} ${selectedAssistant.name}?`}
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteAssistant(selectedAssistant.id)}
      >
        {i18n.t("assistants.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <AssistantModal
        open={assistantModalOpen}
        onClose={handleCloseAssistantModal}
        assistantId={selectedAssistant && selectedAssistant.id}
        assistant={selectedAssistant}
        onAssistantUpdated={handleAssistantUpdated}
      />
      <ImportAssistantsModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImportComplete={handleImportComplete}
      />
      <AssistantsHelpModal
        open={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
      />
      <TrainingModal
        open={trainingModalOpen}
        onClose={handleCloseTrainingModal}
        assistant={assistantForTraining}
        onTrainingComplete={handleTrainingComplete}
      />
    </MainContainer>
  );
};

export default Assistants;